import React, { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Button,
  Checkbox,
  Grid,
  Heading,
  Icon,
  Input,
  Label,
  LinkButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { useSession } from '@eluve/session-helpers';

const TEMPLATES = [
  { value: 'WELCOME', label: 'Welcome' },
  { value: 'SIGNIN_MAGIC_LINK', label: 'Magic Link Sign-in' },
  { value: 'USER_INVITE', label: 'User Invite' },
  { value: 'SUPERBILL', label: 'Superbill' },
] as const;

type TemplateValue = (typeof TEMPLATES)[number]['value'];

const DATADOG_BASE_URL = 'https://us5.datadoghq.com/logs';
const AWS_REGION = 'us-west-1';

const buildDatadogEmailLogsUrl = (messageId?: string): string => {
  const url = new URL(DATADOG_BASE_URL);
  const now = Date.now();
  const oneHourAgo = now - 60 * 60 * 1000;
  const query = messageId
    ? `service:api-nest @component:outgoing-email @messageId:"${messageId}"`
    : `service:api-nest @component:outgoing-email`;
  url.searchParams.set('query', query);
  url.searchParams.set('from_ts', oneHourAgo.toString());
  url.searchParams.set('to_ts', now.toString());
  url.searchParams.set('live', 'true');
  return url.toString();
};

const buildCloudWatchUrl = (messageId: string): string => {
  const logGroup = encodeURIComponent('/ses/transactional/');
  return `https://${AWS_REGION}.console.aws.amazon.com/cloudwatch/home?region=${AWS_REGION}#logsV2:logs-insights$3FqueryDetail$3D~(end~0~start~-3600~timeType~'RELATIVE~tz~'Local~editorString~'fields*20*40timestamp*2c*20*40message*0a*7c*20filter*20*40message*20like*20*22${encodeURIComponent(messageId)}*22~source~(~'${logGroup}*2a))`;
};

export const AdminTestEmailPage: React.FC = () => {
  const apiClient = useApiClient();
  const session = useSession();
  const toast = useToast();
  const [toEmail, setToEmail] = useState(session.email ?? '');
  const [template, setTemplate] = useState<TemplateValue>('WELCOME');
  const [includeTestAttachment, setIncludeTestAttachment] = useState(false);
  const [customFile, setCustomFile] = useState<File | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [lastResult, setLastResult] = useState<{
    success: boolean;
    message: string;
    provider: string;
    messageId?: string;
    sentAt?: number;
  } | null>(null);

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result.split(',')[1] ?? '');
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const handleSend = async () => {
    if (!toEmail) {
      toast.error('Please enter an email address');
      return;
    }

    if (customFile && customFile.size > MAX_FILE_SIZE) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setIsSending(true);
    setLastResult(null);

    try {
      const body: {
        toEmail: string;
        template: TemplateValue;
        includeTestAttachment?: boolean;
        customAttachment?: {
          filename: string;
          contentBase64: string;
          contentType: string;
        };
      } = { toEmail, template };

      if (includeTestAttachment) {
        body.includeTestAttachment = true;
      }

      if (customFile) {
        const contentBase64 = await readFileAsBase64(customFile);
        body.customAttachment = {
          filename: customFile.name,
          contentBase64,
          contentType: customFile.type || 'application/octet-stream',
        };
      }

      const response = await apiClient.adminTestEmail.sendTestEmail({ body });

      if (response.status === 200) {
        setLastResult({ ...response.body, sentAt: Date.now() });
        toast.success(`Test email sent to ${toEmail}`);
      } else {
        toast.error('Failed to send test email');
      }
    } catch {
      toast.error('Failed to send test email');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Grid columns={1} gap={8} className="lg:grid-cols-[1fr_300px]">
      <div className="grid gap-6">
        <div>
          <Heading size="2xl" weight="semibold">
            Test Email
          </Heading>
          <Text>
            Send a test email to verify AWS SES email delivery is working.
          </Text>
        </div>

        <div className="grid max-w-lg gap-4">
          <div className="grid gap-2">
            <Label htmlFor="toEmail">Recipient Email</Label>
            <Input
              id="toEmail"
              type="email"
              placeholder="you@example.com"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="template">Email Template</Label>
            <Select
              value={template}
              onValueChange={(val) => setTemplate(val as TemplateValue)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TEMPLATES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Stack
            direction="horizontal"
            align="center"
            gap={2}
            fullWidth={false}
          >
            <Checkbox
              id="includeTestAttachment"
              checked={includeTestAttachment}
              onCheckedChange={(checked) =>
                setIncludeTestAttachment(checked === true)
              }
            />
            <Label htmlFor="includeTestAttachment">
              Include test attachment (auto-generated .txt file)
            </Label>
          </Stack>

          <div className="grid gap-2">
            <Label htmlFor="customAttachment">
              Custom Attachment (optional)
            </Label>
            <Input
              id="customAttachment"
              type="file"
              onChange={(e) => setCustomFile(e.target.files?.[0] ?? null)}
            />
            {customFile && (
              <p className="text-sm text-text-secondary">
                {customFile.name} ({(customFile.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>

          <Button onClick={handleSend} disabled={isSending || !toEmail}>
            {isSending ? 'Sending...' : 'Send Test Email'}
          </Button>
        </div>

        {lastResult && (
          <div className="max-w-lg rounded-md border p-4">
            <p className="font-medium">
              {lastResult.success ? 'Email sent successfully' : 'Email failed'}
            </p>
            <div className="mt-2 grid gap-1 text-sm text-text-secondary">
              <p>Provider: {lastResult.provider}</p>
              {lastResult.messageId && (
                <p className="break-all">Message ID: {lastResult.messageId}</p>
              )}
              <p>{lastResult.message}</p>
            </div>
            {lastResult.messageId && (
              <Stack
                className="mt-3"
                direction="horizontal"
                wrap
                gap={2}
                align="stretch"
              >
                <LinkButton
                  variant="outline"
                  href={buildDatadogEmailLogsUrl(lastResult.messageId)}
                >
                  <Icon name="Bone" size="auto" />
                  Datadog Logs (this email) ↗
                </LinkButton>
                <LinkButton
                  variant="outline"
                  href={buildCloudWatchUrl(lastResult.messageId)}
                >
                  <Icon name="CloudUpload" size="auto" />
                  CloudWatch SES Events ↗
                </LinkButton>
              </Stack>
            )}
          </div>
        )}
      </div>

      <div className="grid h-fit gap-3 rounded-md border p-4">
        <p className="text-sm font-medium">Email Debugging Tools</p>
        <div className="grid gap-2">
          <LinkButton variant="outline" href={buildDatadogEmailLogsUrl()}>
            <Icon name="Bone" size="auto" />
            Datadog Logs (all emails) ↗
          </LinkButton>
          <LinkButton
            variant="outline"
            href={`https://${AWS_REGION}.console.aws.amazon.com/ses/home?region=${AWS_REGION}#/account`}
          >
            <Icon name="CloudUpload" size="auto" />
            AWS SES Console ↗
          </LinkButton>
          <LinkButton
            variant="outline"
            href={`https://${AWS_REGION}.console.aws.amazon.com/cloudwatch/home?region=${AWS_REGION}#logsV2:log-groups$3FlogGroupNameFilter$3D/ses/`}
          >
            <Icon name="CloudUpload" size="auto" />
            CloudWatch SES Logs ↗
          </LinkButton>
        </div>
      </div>
    </Grid>
  );
};
